import React, { useState } from "react";
import "./chipAction.scss";

export type ChipActionProps = {
  /** Chip label comes from children: <Chip>My Label</Chip> */
  children: React.ReactNode;
  /** Controlled selection state. Omit to use uncontrolled mode. */
  selected?: boolean;
  /** Uncontrolled default (only used when `selected` is undefined). */
  defaultSelected?: boolean;
  disabled?: boolean;
  onChange?: (
    nextSelected: boolean,
    event: React.MouseEvent | React.KeyboardEvent
  ) => void;
  className?: string;
  id?: string;
  /** Visual size */
  size?: "sm" | "md";
  /** Optional leading icon */
  leadingIcon?: React.ReactNode;
};

export default function ChipAction({
  children,
  selected,
  defaultSelected = false,
  disabled = false,
  onChange,
  className = "",
  id,
  size = "md",
  leadingIcon,
}: ChipActionProps) {
  const isControlled = typeof selected === "boolean";
  const [internalSelected, setInternalSelected] =
    useState<boolean>(defaultSelected);
  const isSelected = isControlled ? (selected as boolean) : internalSelected;

  const toggle = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (disabled) return;
    const next = !isSelected;
    if (!isControlled) setInternalSelected(next);
    onChange?.(next, e);
  };

  return (
    <button
      type="button"
      id={id}
      className={[
        "chip-action",
        `chip-action--${size}`,
        isSelected ? "is-selected" : "",
        disabled ? "is-disabled" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      aria-pressed={isSelected}
      disabled={disabled}
      onClick={toggle}
      onKeyDown={(e) => {
        if ((e.key === " " || e.key === "Spacebar") && !disabled) {
          e.preventDefault();
          toggle(e);
        }
      }}
    >
      {leadingIcon && (
        <span className="chip-action__icon" aria-hidden="true">
          {leadingIcon}
        </span>
      )}
      <span className="chip-action__label">{children}</span>
    </button>
  );
}
